<template>
  <div class="qgen-review-table">
    <el-table v-loading="loading" :data="drafts" border size="small" empty-text="处理完成的题目会逐批显示">
      <el-table-column type="expand" width="42">
        <template #default="{ row }">
          <div class="qgen-detail">
            <div class="qgen-source">
              <strong>原文</strong>
              <p>{{ row.raw_text || '-' }}</p>
            </div>
            <el-form label-width="86px">
              <el-form-item label="选项">
                <el-input
                  :model-value="row.options_text"
                  type="textarea"
                  :rows="4"
                  :disabled="readonly"
                  @change="patchOptions(row, $event)"
                />
              </el-form-item>
              <el-form-item label="答案说明">
                <el-input
                  :model-value="row.answer_reason || ''"
                  type="textarea"
                  :rows="2"
                  :disabled="readonly"
                  @change="patch(row, { answer_reason: $event })"
                />
              </el-form-item>
            </el-form>
            <div class="qgen-evidence">
              <span v-if="row.structure_source === 'ai_repaired'">
                <strong>结构修复：</strong>{{ row.structure_model || 'AI' }}{{ row.structure_reason ? `（${row.structure_reason}）` : '' }}
              </span>
              <span v-if="row.answer_risk"><strong>答案风险：</strong>{{ row.answer_risk }}</span>
              <span v-if="row.review_reason"><strong>复核说明：</strong>{{ row.review_reason }}</span>
              <span v-if="row.review_risk"><strong>复核风险：</strong>{{ row.review_risk }}</span>
            </div>
            <el-alert
              v-if="row.validation_errors && row.validation_errors.length"
              type="error"
              :closable="false"
              :title="row.validation_errors.join('；')"
            />
          </div>
        </template>
      </el-table-column>

      <el-table-column label="选择" width="58" align="center">
        <template #default="{ row }">
          <el-checkbox
            :model-value="row.selected !== false"
            :disabled="readonly || !!row.committed_question_id"
            @change="toggleSelected(row, $event)"
          />
        </template>
      </el-table-column>

      <el-table-column label="来源" width="92">
        <template #default="{ row }">
          <div class="qgen-source-cell">
            <strong>{{ row.source_number || row.source_sequence }}</strong>
            <small>第 {{ row.chapter_index }} 章</small>
            <el-tag v-if="row.structure_source === 'ai_repaired'" size="small" type="warning">AI 修复结构</el-tag>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="题目" min-width="260">
        <template #default="{ row }">
          <el-input
            :model-value="row.stem"
            type="textarea"
            :rows="2"
            maxlength="500"
            show-word-limit
            :disabled="readonly"
            @change="patch(row, { stem: $event })"
          />
          <el-tag v-if="row.structure_status !== 'valid'" size="small" type="danger">结构待处理</el-tag>
        </template>
      </el-table-column>

      <el-table-column label="题型" width="92" align="center">
        <template #default="{ row }">{{ questionTypeText(row.draft_question_type) }}</template>
      </el-table-column>

      <el-table-column label="答案" min-width="155">
        <template #default="{ row }">
          <div class="qgen-answer-cell">
            <small>{{ answerSourceText(row.answer_source) }}</small>
            <span>原：{{ row.document_answer || '-' }}</span>
            <span>建议：{{ row.proposed_answer || '-' }}</span>
            <el-input
              :model-value="row.final_answer"
              size="small"
              placeholder="输入人工最终答案"
              :disabled="readonly"
              @input="editFinalAnswer(row, $event)"
            />
          </div>
        </template>
      </el-table-column>

      <el-table-column label="入库分类" min-width="150">
        <template #default="{ row }">
          <el-select
            :model-value="row.category_id"
            placeholder="未分类"
            clearable
            filterable
            :disabled="readonly || !!row.committed_question_id"
            @change="patch(row, { category_id: $event || null })"
          >
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.display_name || category.name"
              :value="category.id"
            />
          </el-select>
        </template>
      </el-table-column>

      <el-table-column label="独立复核" width="130" align="center">
        <template #default="{ row }">
          <div class="qgen-review-cell">
            <el-tag size="small" :type="reviewTagType(row.review_status)">{{ reviewStatusText(row.review_status) }}</el-tag>
            <small v-if="row.review_answer">答案 {{ row.review_answer }}</small>
            <small v-if="row.review_confidence != null">置信度 {{ Math.round(Number(row.review_confidence) * 100) }}%</small>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="人工确认" width="118" align="center">
        <template #default="{ row }">
          <div class="qgen-manual-cell">
            <el-tag size="small" :type="manualTagType(row.manual_status)">{{ manualStatusText(row.manual_status) }}</el-tag>
            <div v-if="!readonly && !row.committed_question_id" class="qgen-icon-actions">
              <el-tooltip content="确认人工最终答案（冲突时以此为准）">
                <el-button circle size="small" type="success" :icon="Check" @click="confirm(row)" />
              </el-tooltip>
              <el-tooltip content="跳过此题">
                <el-button circle size="small" :icon="CloseBold" @click="bulk('skip', [row.draft_id])" />
              </el-tooltip>
              <el-tooltip content="重新复核">
                <el-button circle size="small" type="primary" :icon="RefreshRight" @click="retry('reviewing', [row.draft_id])" />
              </el-tooltip>
            </div>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="qgen-pagination"
      background
      layout="total, sizes, prev, pager, next"
      :current-page="page"
      :page-size="pageSize"
      :page-sizes="[20, 40, 80]"
      :total="total"
      @current-change="emit('page-change', $event)"
      @size-change="emit('size-change', $event)"
    />
  </div>
</template>

<script setup>
import { Check, CloseBold, RefreshRight } from '@element-plus/icons-vue'

defineProps({
  drafts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  readonly: { type: Boolean, default: true },
  page: { type: Number, default: 1 },
  pageSize: { type: Number, default: 20 },
  total: { type: Number, default: 0 },
  categories: { type: Array, default: () => [] }
})
const emit = defineEmits(['patch', 'bulk', 'retry', 'answer-input', 'confirm', 'page-change', 'size-change'])

const patch = (row, changes) => emit('patch', row, changes)
const bulk = (action, ids) => emit('bulk', action, ids)
const retry = (stage, ids) => emit('retry', stage, ids)
const editFinalAnswer = (row, value) => emit('answer-input', row, value)
const confirm = (row) => emit('confirm', row)
const toggleSelected = (row, selected) => bulk(selected ? 'select' : 'unselect', [row.draft_id])

const patchOptions = (row, value) => {
  const options = String(value || '').split(/[\n|；;]+/).map((part, index) => {
    const match = part.trim().match(/^([A-Ha-h])[.、．:：)]\s*(.+)$/)
    return match
      ? { key: match[1].toUpperCase(), value: match[2].trim() }
      : { key: String.fromCharCode(65 + index), value: part.trim() }
  }).filter((item) => item.value)
  patch(row, { options_json: options })
}

const questionTypeText = (type) => ({ single_choice: '单选题', multiple_choice: '多选题', true_false: '判断题' }[type] || '待识别')
const answerSourceText = (source) => ({ document: '文档答案', ai_inferred: 'AI 补全', teacher: '教师答案', missing: '缺少答案' }[source] || '未知来源')
const reviewStatusText = (status) => ({
  pending: '待复核', agree: '一致', conflict: '冲突', uncertain: '不确定',
  source_conflict: '原答案存疑', review_unavailable: '复核不可用', failed: '复核失败'
}[status] || '待复核')
const reviewTagType = (status) => ({ agree: 'success', conflict: 'danger', source_conflict: 'danger', uncertain: 'warning', review_unavailable: 'warning', failed: 'danger' }[status] || 'info')
const manualStatusText = (status) => ({ pending: '待确认', confirmed: '已确认', skipped: '已跳过' }[status] || '待确认')
const manualTagType = (status) => ({ confirmed: 'success', skipped: 'info' }[status] || 'warning')
</script>

<style scoped>
.qgen-review-table { width: 100%; }
.qgen-detail { padding: 12px 16px 2px; background: #fafcff; }
.qgen-source { margin-bottom: 12px; }
.qgen-source p { margin: 5px 0 0; color: #606266; line-height: 1.55; white-space: pre-wrap; }
.qgen-evidence { display: grid; gap: 6px; margin: 8px 0 12px; color: #606266; font-size: 12px; }
.qgen-source-cell, .qgen-answer-cell, .qgen-review-cell, .qgen-manual-cell { display: flex; flex-direction: column; gap: 5px; }
.qgen-source-cell small, .qgen-answer-cell small, .qgen-review-cell small { color: #909399; }
.qgen-icon-actions { display: flex; justify-content: center; gap: 5px; margin-top: 3px; }
.qgen-pagination { justify-content: flex-end; margin-top: 14px; }
</style>
